import { FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Fragment } from "react/jsx-runtime";

import { useUpdatePartWeight } from "#modules/parts/view/useUpdatePartWeight";
import { Flex } from "#shared/view/components/Flex";
import { Modal } from "#shared/view/components/Modal";
import { Stack } from "#shared/view/components/Stack";
import { Button } from "#shared/view/designSystem/Button";
import { NumberInput } from "#shared/view/designSystem/inputs/NumberInput";
import { MaterialGradeType, ExtendedPart } from "shared";

type Props = {
  part: ExtendedPart | null | undefined;
  weight?: number;
  partWeightModalOpened: boolean;
  closePartWeightModal: () => void;
  onSubmit?: () => void;
  quoteRequestLineId: string;
  materialGrade: MaterialGradeType;
};

export const PartWeightModal = ({
  part,
  weight,
  partWeightModalOpened,
  closePartWeightModal,
  onSubmit,
  quoteRequestLineId,
}: Props) => {
  const { t } = useTranslation();
  const [editedWeight, setEditedWeight] = useState<number | undefined>(weight);

  const { updatePartWeight } = useUpdatePartWeight({
    partId: part?.id ?? "",
    options: {
      onSuccess: () => {
        onSubmit?.();
      },
    },
  });

  useEffect(() => {
    setEditedWeight(weight);
  }, [weight]);

  if (!part?.id) {
    return null;
  }

  const handleCancel = () => {
    setEditedWeight(weight);
    closePartWeightModal();
  };

  const handleValidate = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!editedWeight || editedWeight <= 0) {
      return;
    }

    updatePartWeight({
      id: part.id,
      data: {
        weight: editedWeight,
        quoteRequestLineId,
      },
    });

    closePartWeightModal();
  };

  return (
    <Fragment>
      <Modal
        opened={partWeightModalOpened}
        withCloseButton={false}
        onClose={closePartWeightModal}
        size="50%"
        title={t("qrl.part.weightTitle")}
      >
        <form onSubmit={handleValidate}>
          <Stack gap="sm">
            <NumberInput
              suffix=" kg"
              placeholder="en kg"
              label={t("qrl.part.weight")}
              name="weight"
              value={editedWeight}
              onChange={setEditedWeight}
              min={0.01}
            />

            <Flex gap="xs" justify="end">
              <Button variant="outline" onClick={handleCancel}>
                {t("common.cancel")}
              </Button>
              <Button type="submit">{t("common.validate")}</Button>
            </Flex>
          </Stack>
        </form>
      </Modal>
    </Fragment>
  );
};
